/**
 * Public Signing Page for Customers
 * 
 * This page allows customers to:
 * 1. View the offer PDF
 * 2. Verify identity with OTP
 * 3. Sign digitally or upload a signed document
 * 
 * No authentication required - access via secure token in URL
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  FileText, 
  CheckCircle, 
  Shield, 
  Upload, 
  Download,
  Loader2,
  Mail,
  Pen,
  AlertCircle,
  Eye,
  RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SigningPage() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [step, setStep] = useState("view"); // view, otp, sign, complete
  
  // OTP State
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  
  // Signature State
  const [signatureMethod, setSignatureMethod] = useState("digital"); // digital or upload
  const [confirmationsAccepted, setConfirmationsAccepted] = useState([]);
  const [typedName, setTypedName] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Canvas ref for signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Fetch page data
  const fetchPageData = useCallback(async (isInitialLoad = false) => {
    if (!requestId || !token) {
      setError("Ungültiger Link - fehlende Parameter");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/sign/${requestId}?token=${encodeURIComponent(token)}`);
      
      // Parse response safely
      let data = null;
      let errorMessage = "Fehler beim Laden der Daten";
      
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } catch (parseErr) {
        console.log("Response parse error");
      }
      
      if (!response.ok) {
        // Extract detailed error message from backend
        let errorMessage = data?.detail || data?.message;
        
        // Provide user-friendly messages for common HTTP status codes
        if (!errorMessage) {
          switch (response.status) {
            case 401:
              errorMessage = "Ungültiger oder abgelaufener Link. Bitte fordern Sie einen neuen Signatur-Link an.";
              break;
            case 404:
              errorMessage = "Signaturanfrage nicht gefunden. Bitte prüfen Sie, ob das Angebot zur Unterschrift versendet wurde.";
              break;
            case 410:
              errorMessage = "Dieser Link ist abgelaufen oder wurde widerrufen.";
              break;
            case 429:
              errorMessage = "Zu viele Anfragen. Bitte warten Sie einen Moment.";
              break;
            default:
              errorMessage = `Fehler ${response.status}: ${response.statusText || 'Unbekannter Fehler'}`;
          }
        }
        
        console.error("Backend error:", response.status, errorMessage);
        throw new Error(String(errorMessage));
      }
      
      if (!data) {
        throw new Error("Keine Daten erhalten");
      }
      
      setPageData(data);
      
      // Determine current step based on status
      // Set initial step based on status
      // IMPORTANT: Don't override step if user is in the middle of OTP verification
      if (data.status === "completed_digital" || data.status === "completed_manual") {
        setStep("complete");
      } else if (data.otp_verified) {
        setStep("sign");
      } else if (isInitialLoad) {
        // Only set to "view" on initial load, not on subsequent fetches
        setStep("view");
      }
      
      setError(null);
    } catch (err) {
      console.error("Fetch page data error:", err);
      // Only store string error message, never error object
      setError(String(err?.message || err || "Fehler beim Laden"));
    } finally {
      setLoading(false);
    }
  }, [requestId, token]);

  useEffect(() => {
    fetchPageData(true); // Initial load
  }, [fetchPageData]);

  // OTP Countdown timer
  useEffect(() => {
    if (otpResendCountdown > 0) {
      const timer = setTimeout(() => setOtpResendCountdown(otpResendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendCountdown]);

  // Request OTP
  const handleRequestOtp = async (e) => {
    // Prevent any default behavior (Safari fix)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setOtpLoading(true);
    
    // Immediately switch to OTP step (optimistic update)
    setStep("otp");
    
    try {
      console.log("Requesting OTP for:", requestId);
      const response = await fetch(
        `${API_URL}/api/sign/${requestId}/request-otp?token=${encodeURIComponent(token)}`,
        { 
          method: "POST",
          headers: { "Content-Type": "application/json" }
        }
      );
      
      let responseData = null;
      try {
        const text = await response.text();
        console.log("OTP response text:", text);
        if (text) {
          responseData = JSON.parse(text);
        }
      } catch (parseErr) {
        console.log("OTP response parse error - non-JSON response");
      }
      
      if (!response.ok) {
        const errorMsg = responseData?.detail || responseData?.message || "Fehler beim Senden des Codes";
        // Go back to view step on error
        setStep("view");
        throw new Error(String(errorMsg));
      }
      
      console.log("OTP request successful");
      setOtpSent(true);
      setOtpResendCountdown(60); // 60 seconds cooldown
      toast.success("Bestätigungscode wurde per E-Mail gesendet");
      
    } catch (err) {
      console.error("OTP request error:", err);
      toast.error(err.message || "Fehler beim Senden des Codes");
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async (e) => {
    // Prevent any default behavior (Safari fix)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Bitte geben Sie den 6-stelligen Code ein");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/sign/${requestId}/verify-otp?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp_code: otpCode })
        }
      );
      
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (parseErr) {
        // Response might not be JSON
      }
      
      if (!response.ok) {
        throw new Error(errorData.detail || errorData.message || "Ungültiger Code");
      }
      
      toast.success("Code verifiziert!");
      setPageData(prev => ({ ...prev, otp_verified: true }));
      setStep("sign");
    } catch (err) {
      console.error("OTP verify error:", err);
      toast.error(err.message || "Ungültiger Code");
    } finally {
      setOtpLoading(false);
    }
  };

  // Canvas drawing functions - with proper coordinate scaling for touch and high-DPI
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    
    // Get the touch or mouse coordinates
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    
    // Calculate position relative to displayed canvas
    // Note: We DON'T multiply by DPR here because ctx.scale(dpr, dpr) handles it
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    return { x, y };
  };

  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    // Save signature data
    const canvas = canvasRef.current;
    setSignatureData(canvas.toDataURL("image/png"));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    
    // Reset transform before clearing (in case DPR scaling was applied)
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Re-apply DPR scaling and styles
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    setSignatureData(null);
  };

  // Initialize canvas with proper sizing for high-DPI displays
  useEffect(() => {
    if (step === "sign" && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      
      // Get the displayed size
      const rect = canvas.getBoundingClientRect();
      
      // Get device pixel ratio for sharp rendering on high-DPI screens
      const dpr = window.devicePixelRatio || 1;
      
      // Set canvas internal dimensions to match displayed size * DPR
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Scale context to handle DPR
      ctx.scale(dpr, dpr);
      
      // Set drawing styles
      ctx.strokeStyle = "#1f2937";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [step]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PDF, JPG oder PNG erlaubt");
      return;
    }
    
    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Datei zu groß (max 20 MB)");
      return;
    }
    
    setUploadedFile(file);
  };

  // Submit signature
  const handleSubmitSignature = async (e) => {
    // Prevent default and stop propagation (iOS fix)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Validate confirmations
    if (pageData.confirmation_texts && confirmationsAccepted.length !== pageData.confirmation_texts.length) {
      toast.error("Bitte bestätigen Sie alle Punkte");
      return;
    }

    if (signatureMethod === "digital") {
      if (!signatureData) {
        toast.error("Bitte zeichnen Sie Ihre Unterschrift");
        return;
      }

      setSubmitting(true);
      try {
        const response = await fetch(
          `${API_URL}/api/sign/${requestId}/sign-digital?token=${encodeURIComponent(token)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              signature_data: signatureData,
              typed_name: typedName,
              confirmation_accepted: true
            })
          }
        );
        
        // Parse response safely
        let responseData = null;
        let errorMessage = "Fehler beim Signieren";
        
        try {
          const text = await response.text();
          if (text) {
            responseData = JSON.parse(text);
          }
        } catch (parseErr) {
          console.log("Response parse error (non-JSON response)");
        }
        
        if (!response.ok) {
          errorMessage = responseData?.detail || responseData?.message || errorMessage;
          throw new Error(String(errorMessage));
        }
        
        console.log("Digital signature successful:", response.status);
        toast.success("Erfolgreich unterschrieben!");
        setStep("complete");
        
        // Refresh data after short delay
        setTimeout(() => {
          fetchPageData();
        }, 500);
      } catch (err) {
        console.error("Sign error:", err);
        // Only use string message, never store error object
        const errMsg = String(err?.message || err || "Fehler beim Signieren");
        toast.error(errMsg);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Manual upload
      if (!uploadedFile) {
        toast.error("Bitte laden Sie das unterschriebene Dokument hoch");
        return;
      }

      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        
        const response = await fetch(
          `${API_URL}/api/sign/${requestId}/upload-manual?token=${encodeURIComponent(token)}`,
          {
            method: "POST",
            body: formData
          }
        );
        
        // Parse response safely
        let responseData = null;
        let errorMessage = "Fehler beim Hochladen";
        
        try {
          const text = await response.text();
          if (text) {
            responseData = JSON.parse(text);
          }
        } catch (parseErr) {
          console.log("Response parse error (non-JSON response)");
        }
        
        if (!response.ok) {
          errorMessage = responseData?.detail || responseData?.message || errorMessage;
          throw new Error(String(errorMessage));
        }
        
        console.log("Manual upload successful:", response.status);
        toast.success("Dokument erfolgreich hochgeladen!");
        setStep("complete");
        
        // Refresh data after short delay
        setTimeout(() => {
          fetchPageData();
        }, 500);
      } catch (err) {
        console.error("Upload error:", err);
        // Only use string message, never store error object
        const errMsg = String(err?.message || err || "Fehler beim Hochladen");
        toast.error(errMsg);
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Toggle confirmation checkbox
  const toggleConfirmation = (index) => {
    setConfirmationsAccepted(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center" data-testid="signing-page-loading">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error) {
    // Check if it's a token-related error (401 or specific message)
    const isTokenError = error.includes("Ungültiger") || error.includes("abgelaufen") || error.includes("401");
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="signing-page-error">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <CardTitle className="text-destructive">Fehler</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{error}</p>
            
            {isTokenError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-amber-800 mb-2">Was können Sie tun?</p>
                <ul className="text-amber-700 space-y-1 list-disc list-inside">
                  <li>Verwenden Sie den Link aus der <strong>neuesten E-Mail</strong></li>
                  <li>QR-Codes im PDF können ungültig werden, wenn ein neuer Link generiert wurde</li>
                  <li>Kontaktieren Sie den Absender, um einen neuen Link zu erhalten</li>
                </ul>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Erneut versuchen
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // No page data state (fallback)
  if (!pageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="signing-page-no-data">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Laden...</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Bitte warten Sie, während die Daten geladen werden.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="signing-page">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {pageData?.company_name || "Angebot"}
            </h1>
            <p className="text-sm text-slate-500">Digitale Unterschrift</p>
          </div>
          <Badge variant={step === "complete" ? "success" : "outline"}>
            {step === "complete" ? "Abgeschlossen" : `Schritt ${step === "view" ? "1" : step === "otp" ? "2" : "3"} von 3`}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { key: "view", label: "Ansehen", icon: Eye },
            { key: "otp", label: "Verifizieren", icon: Shield },
            { key: "sign", label: "Unterschreiben", icon: Pen },
          ].map((s, i) => (
            <div key={s.key} className="flex items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full transition-colors
                ${step === s.key ? "bg-primary text-primary-foreground" : ""}
                ${["otp", "sign", "complete"].includes(step) && i === 0 ? "bg-green-500 text-white" : ""}
                ${["sign", "complete"].includes(step) && i === 1 ? "bg-green-500 text-white" : ""}
                ${step === "complete" && i === 2 ? "bg-green-500 text-white" : ""}
                ${!["view", "otp", "sign", "complete"].includes(step) || (step === "view" && i > 0) || (step === "otp" && i > 1) ? "bg-slate-200 text-slate-500" : ""}
              `}>
                {(["otp", "sign", "complete"].includes(step) && i === 0) ||
                 (["sign", "complete"].includes(step) && i === 1) ||
                 (step === "complete" && i === 2) ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <s.icon className="h-5 w-5" />
                )}
              </div>
              <span className={`ml-2 text-sm hidden sm:block ${step === s.key ? "font-medium text-slate-900" : "text-slate-500"}`}>
                {s.label}
              </span>
              {i < 2 && <div className="w-8 sm:w-16 h-px bg-slate-300 ml-4" />}
            </div>
          ))}
        </div>

        {/* Completed State */}
        {step === "complete" && (
          <Card className="border-green-200 bg-green-50" data-testid="signing-complete">
            <CardHeader>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <div>
                  <CardTitle className="text-green-800">Erfolgreich unterschrieben!</CardTitle>
                  <CardDescription className="text-green-700">
                    Vielen Dank für Ihre Unterschrift.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Angebotsnummer:</dt>
                    <dd className="font-medium text-slate-900">{pageData?.offer_number}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Unterzeichner:</dt>
                    <dd className="font-medium text-slate-900">{pageData?.recipient_name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Betrag:</dt>
                    <dd className="font-medium text-green-700">{pageData?.total_price}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Status:</dt>
                    <dd className="font-medium text-green-600">Abgeschlossen</dd>
                  </div>
                  {pageData?.completed_at && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Abgeschlossen am:</dt>
                      <dd className="font-medium text-slate-900">
                        {new Date(pageData.completed_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
              
              {/* Download Link for signed PDF */}
              {pageData?.signed_pdf_download_url && (
                <div className="mt-4">
                  <a 
                    href={`${API_URL}${pageData.signed_pdf_download_url}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                    data-testid="download-signed-pdf-btn"
                  >
                    <Download className="h-4 w-4" />
                    Signiertes Dokument herunterladen
                  </a>
                </div>
              )}
              
              <p className="mt-4 text-sm text-slate-600">
                Sie erhalten in Kürze eine Bestätigungs-E-Mail mit dem unterschriebenen Dokument.
              </p>
            </CardContent>
          </Card>
        )}

        {/* View Offer Step */}
        {step === "view" && (
          <div className="space-y-6">
            <Card data-testid="offer-preview">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Angebot {pageData?.offer_number}
                </CardTitle>
                <CardDescription>
                  Bitte prüfen Sie das Angebot vor der Unterschrift
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-100 rounded-lg p-4 space-y-3 text-slate-900">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Firma:</span>
                    <span className="font-semibold text-slate-900">{pageData?.company_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Empfänger:</span>
                    <span className="font-semibold text-slate-900">{pageData?.recipient_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Datum:</span>
                    <span className="font-semibold text-slate-900">{pageData?.offer_date}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Gesamtbetrag (brutto):</span>
                    <span className="font-bold text-lg text-green-700">{pageData?.total_price}</span>
                  </div>
                </div>
                
                {/* PDF Preview Link - PROMINENT */}
                <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                  <p className="text-red-700 font-semibold text-lg mb-3">
                    Bitte prüfen Sie das Angebot vor der Unterschrift!
                  </p>
                  <a 
                    href={`${API_URL}${pageData?.pdf_preview_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-lg transition-colors shadow-md"
                    data-testid="view-pdf-link"
                  >
                    <Eye className="h-6 w-6" />
                    Angebot als PDF öffnen
                  </a>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  type="button"
                  onClick={handleRequestOtp} 
                  disabled={otpLoading}
                  className="w-full"
                  data-testid="start-signing-btn"
                >
                  {otpLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Mit E-Mail-Code verifizieren
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        )}

        {/* OTP Verification Step */}
        {step === "otp" && (
          <Card data-testid="otp-verification">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                E-Mail Bestätigung
              </CardTitle>
              <CardDescription>
                Wir haben einen 6-stelligen Code an Ihre E-Mail-Adresse gesendet.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Bestätigungscode</label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>
              
              <div className="text-center text-sm text-slate-500">
                {otpResendCountdown > 0 ? (
                  <span>Neuen Code anfordern in {otpResendCountdown}s</span>
                ) : (
                  <button 
                    type="button"
                    onClick={handleRequestOtp}
                    disabled={otpLoading}
                    className="text-primary hover:underline"
                    data-testid="resend-otp-btn"
                  >
                    Code erneut senden
                  </button>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep("view")}>
                Zurück
              </Button>
              <Button 
                type="button"
                onClick={handleVerifyOtp} 
                disabled={otpLoading || otpCode.length !== 6}
                className="flex-1"
                data-testid="verify-otp-btn"
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird geprüft...
                  </>
                ) : (
                  "Code bestätigen"
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Signature Step */}
        {step === "sign" && (
          <div className="space-y-6">
            {/* Signature Method Selection */}
            <Card data-testid="signature-method">
              <CardHeader>
                <CardTitle>Unterschriftsmethode wählen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setSignatureMethod("digital")}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      signatureMethod === "digital" 
                        ? "border-primary bg-primary/5" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="method-digital"
                  >
                    <Pen className={`h-6 w-6 mb-2 ${signatureMethod === "digital" ? "text-primary" : "text-slate-500"}`} />
                    <div className="font-medium">Digital unterschreiben</div>
                    <div className="text-sm text-slate-500">Direkt auf dem Bildschirm</div>
                  </button>
                  <button
                    onClick={() => setSignatureMethod("upload")}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      signatureMethod === "upload" 
                        ? "border-primary bg-primary/5" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="method-upload"
                  >
                    <Upload className={`h-6 w-6 mb-2 ${signatureMethod === "upload" ? "text-primary" : "text-slate-500"}`} />
                    <div className="font-medium">Dokument hochladen</div>
                    <div className="text-sm text-slate-500">PDF mit Ihrer Unterschrift</div>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Digital Signature */}
            {signatureMethod === "digital" && (
              <Card data-testid="digital-signature-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pen className="h-5 w-5" />
                    Ihre Unterschrift
                  </CardTitle>
                  <CardDescription>
                    Zeichnen Sie Ihre Unterschrift im Feld unten
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative bg-white border-2 border-slate-200 rounded-lg overflow-hidden">
                    <canvas
                      ref={canvasRef}
                      className="w-full touch-none cursor-crosshair"
                      style={{ height: '200px' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      data-testid="signature-canvas"
                    />
                    <div className="absolute bottom-2 left-2 right-2 h-px bg-slate-300 pointer-events-none" />
                    <span className="absolute bottom-3 left-4 text-xs text-slate-400 pointer-events-none">
                      Hier unterschreiben
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <Button variant="outline" size="sm" onClick={clearSignature} data-testid="clear-signature-btn">
                      Löschen
                    </Button>
                    {signatureData && (
                      <Badge variant="success" className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Unterschrift erfasst
                      </Badge>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Name bestätigen (optional)</label>
                    <Input
                      placeholder="Max Mustermann"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      data-testid="typed-name-input"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upload */}
            {signatureMethod === "upload" && (
              <Card data-testid="upload-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Unterschriebenes Dokument
                  </CardTitle>
                  <CardDescription>
                    Laden Sie das Angebot mit Ihrer handschriftlichen Unterschrift hoch
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="mb-4">
                    <a 
                      href={`${API_URL}${pageData?.pdf_preview_url}`}
                      download
                      className="inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      Angebot herunterladen zum Unterschreiben
                    </a>
                  </div>
                  
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="file-upload-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                      <p className="text-slate-600 mb-1">
                        {uploadedFile ? uploadedFile.name : "Datei auswählen oder hierher ziehen"}
                      </p>
                      <p className="text-sm text-slate-400">PDF, JPG oder PNG (max. 20 MB)</p>
                    </label>
                  </div>
                  
                  {uploadedFile && (
                    <Badge variant="success" className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {uploadedFile.name}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Confirmations */}
            <Card data-testid="confirmations-card">
              <CardHeader>
                <CardTitle>Bestätigungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pageData?.confirmation_texts?.map((text, index) => (
                  <label key={index} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmationsAccepted.includes(index)}
                      onChange={() => toggleConfirmation(index)}
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      data-testid={`confirmation-${index}`}
                    />
                    <span className="text-sm text-slate-700">{text}</span>
                  </label>
                ))}
              </CardContent>
              <CardFooter>
                <Button 
                  type="button"
                  onClick={handleSubmitSignature}
                  disabled={submitting || (signatureMethod === "digital" && !signatureData) || (signatureMethod === "upload" && !uploadedFile) || confirmationsAccepted.length !== (pageData?.confirmation_texts?.length || 0)}
                  className="w-full"
                  data-testid="submit-signature-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Verbindlich unterschreiben
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto py-4">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>Diese Unterschrift ist rechtlich bindend gemäß den geltenden Vorschriften für elektronische Signaturen.</p>
        </div>
      </footer>
    </div>
  );
}
